/**
 * Message requests waiting on me (PPL-429).
 *
 * One shared poller for the whole app, on the pattern `useUnreadMessages`
 * already uses: several components want this number and none of them should
 * cost their own request. It skips a hidden tab, never overlaps a slow call
 * with the next tick, and stops entirely when nobody is subscribed.
 *
 * The prompt is shown ONCE per request. Which ones have been shown lives in
 * localStorage rather than on the server, because it is a per-device
 * convenience, not state: the durable home of a request is the inbox section,
 * and a person who dismisses the prompt has lost nothing.
 */
import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getMessageRequests } from '../services/messageService';
import type { MessageRequest } from '../types/social';

const POLL_INTERVAL_MS = 30000;
const PROMPTED_KEY = 'pplkpr.mr.prompted';
const PROMPTED_MAX = 60;

let shared: MessageRequest[] = [];
// Whether a poll has come back for the CURRENT user yet. An empty list before
// the first result means "not known", not "nothing waiting" — the difference
// decides whether a conversation shows its accept/decline banner.
let loaded = false;
let currentUserId: string | undefined;
let intervalId: ReturnType<typeof setInterval> | null = null;
let inFlight = false;
const subscribers = new Set<(r: MessageRequest[]) => void>();
const loadSubscribers = new Set<(l: boolean) => void>();

function notify() {
  for (const cb of subscribers) cb(shared);
  for (const cb of loadSubscribers) cb(loaded);
}

/** Chat ids already prompted on this device. Storage can throw — never let it matter. */
function promptedIds(): string[] {
  try {
    const raw = localStorage.getItem(PROMPTED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function markPrompted(chatId: string): void {
  try {
    const next = [chatId, ...promptedIds().filter((id) => id !== chatId)].slice(0, PROMPTED_MAX);
    localStorage.setItem(PROMPTED_KEY, JSON.stringify(next));
  } catch {
    /* a private window is not a reason to fail */
  }
}

export function hasBeenPrompted(chatId: string): boolean {
  return promptedIds().includes(chatId);
}

async function fetchRequests() {
  if (inFlight) return;
  if (typeof document !== 'undefined' && document.hidden) return;
  inFlight = true;
  // Whose requests these are. A poll that started before a sign-out must not
  // land in the next person's cache: they would see the previous user's
  // handle, city and message until their own fetch returned.
  const forUid = currentUserId;
  try {
    const requests = await getMessageRequests();
    if (forUid !== currentUserId) return;
    const changed =
      !loaded ||
      requests.length !== shared.length ||
      requests.some((r, i) => r.chatId !== shared[i]?.chatId);
    shared = requests;
    loaded = true;
    if (changed) notify();
  } catch {
    // A failed poll is not worth telling anyone about; the next tick retries.
  } finally {
    inFlight = false;
  }
}

function start() {
  if (intervalId) return;
  fetchRequests();
  intervalId = setInterval(fetchRequests, POLL_INTERVAL_MS);
}

function stop() {
  if (intervalId) clearInterval(intervalId);
  intervalId = null;
}

/**
 * @returns every request waiting on me, newest first, and whether that list
 * has been fetched yet for this user.
 */
export function useMessageRequests(): {
  requests: MessageRequest[];
  loaded: boolean;
  refresh: () => void;
} {
  const [requests, setRequests] = useState<MessageRequest[]>(shared);
  const [isLoaded, setIsLoaded] = useState<boolean>(loaded);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(getAuth(), (user) => {
      if (user?.uid === currentUserId) return;
      currentUserId = user?.uid;
      shared = [];
      loaded = false;
      notify();
      if (user) start();
      else stop();
    });
    return unsubAuth;
  }, []);

  useEffect(() => {
    subscribers.add(setRequests);
    loadSubscribers.add(setIsLoaded);
    if (currentUserId) start();
    return () => {
      subscribers.delete(setRequests);
      loadSubscribers.delete(setIsLoaded);
      if (subscribers.size === 0) stop();
    };
  }, []);

  // `forgetRequest` is what drops an answered request at once; this only asks
  // the server again, for a caller that wants to be sure.
  return {requests, loaded: isLoaded, refresh: fetchRequests};
}

/** Removes one request from the shared list immediately after it is answered. */
export function forgetRequest(chatId: string): void {
  const next = shared.filter((r) => r.chatId !== chatId);
  if (next.length === shared.length) return;
  shared = next;
  notify();
}
